import { Select, Tag } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { IEntitySimpleDTO } from "../../libs/coreapi-dto/@types/common";
import styles from "./entityMultiSelect.module.scss"
import { GridFilterAction } from "../../system/hooks/useGridFilter";

interface IEntityMultiSelectProps {
    loading?: boolean;
    disabled?: boolean;
    items: IEntitySimpleDTO[];
    filterName: string;
    gridFilter: IGridFilter;
    dispatchGridFilter: (value: GridFilterAction) => void;
    handleSearchChange?: (selectedItems: ISelectedItems) => void;
    placeholder: string;
    selectedItems?: IEntitySimpleDTO[];
    showClearButton?: boolean
}

interface ISelectedItems {
    ids: string[];
    names: string[];
    items: IEntitySimpleDTO[];
}

type CustomTagProps = {
    label: React.ReactNode;
    value: any;
    disabled: boolean;
    onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    closable: boolean;
};

const EntityMultiSelect: FC<IEntityMultiSelectProps> = ({
    loading,
    disabled,
    items,
    filterName,
    gridFilter,
    dispatchGridFilter,
    handleSearchChange,
    placeholder,
    selectedItems: externalSelectedItems,
    showClearButton
}) => {
    const [selectedItemsIds, setSelectedItemsIds] = useState<string[]>([]);

    const { Option } = Select;

    const itemMap = useMemo(() => {
        const map = new Map<string, string>();
        items.forEach(item => {
            map.set(item.idGlobal, item.displayName);
        });
        return map;
    }, [items]);

    useEffect(() => {
        if (externalSelectedItems) {
            const ids = externalSelectedItems.map(item => item.idGlobal);
            setSelectedItemsIds(ids);
        }
    }, [externalSelectedItems]);

    const filterOption = (inputValue: string, option: any) => {
        const itemName = itemMap.get(option.key);
        return itemName?.toLowerCase().includes(inputValue.toLowerCase()) || false;
    };

    const tagRender = (props: CustomTagProps) => {
        const { label, value, closable, onClose } = props;
        const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
        };

        const displayName = itemMap.get(value) || label;

        return (
            <Tag
                onMouseDown={onPreventMouseDown}
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3 }}
            >
                {displayName}
            </Tag>
        );
    };

    const handleItemsChange = useCallback((items: IEntitySimpleDTO[]) => {

        dispatchGridFilter({
            type: "deleteSpecificParamFilter",
            payload: filterName
        });

        if (items.length > 0) {
            const itemsIds = items.map(item => item.idGlobal);                        
            const itemIdsString = itemsIds.join(','); // если бэк будет ожидать строку.

            if (gridFilter.paramFilter) {
                dispatchGridFilter({
                    type: "addParamFilter",
                    payload: {
                        [filterName]: itemsIds
                    }
                });
            } else {
                dispatchGridFilter({
                    type: "paramSimpleFilter",
                    payload: {
                        gridColumnFilter: gridFilter.columnFilters,
                        gridParamFilter: {
                            [filterName]: itemsIds
                        }
                    }
                });
            }
        }
    }, [dispatchGridFilter, gridFilter.columnFilters, gridFilter.paramFilter]);

    const handleItemsIdChange = (selectedIds: string[]) => {
        setSelectedItemsIds(selectedIds);

        const selectedItems = items.filter(item =>
            selectedIds.includes(item.idGlobal)
        );

        if (handleSearchChange) {
            handleSearchChange({
                ids: selectedIds,
                names: selectedItems.map(item => item.displayName),
                items: selectedItems
            });
        }
        
        if (selectedItems.length > 0) {
            handleItemsChange(selectedItems);
        } else {
            dispatchGridFilter({
                type: "deleteSpecificParamFilter",
                payload: filterName
            });
        }
    };

    const hasSelectedItems = selectedItemsIds.length > 0;

    return (
        <Select
            className={hasSelectedItems ? `${styles.selectFilter} has-selected-items` : styles.selectFilter}
            mode="multiple"
            showArrow
            showSearch
            loading={loading}
            disabled={disabled}
            placeholder={placeholder}
            tagRender={tagRender}
            value={selectedItemsIds}
            onChange={handleItemsIdChange}
            optionLabelProp="label"
            filterOption={filterOption}
            optionFilterProp="children"
            allowClear={showClearButton}
        >
            {items.map(item => (
                <Option
                    key={item.idGlobal}
                    value={item.idGlobal}
                    label={item.displayName}
                >
                    {item.displayName}
                </Option>
            ))}
        </Select>
    )
}

export default EntityMultiSelect